import type { AIResultVisualBlockCounts } from '@atlaskit/editor-common/analytics/types/ai-unified-events';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics/analyticsPluginType';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/editor-experiment';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { AdfParseError } from '@atlassian/adf-parser/errors';
import { AdfParser } from '@atlassian/adf-parser/parser';
import { AdfRepairStrategy } from '@atlassian/adf-parser/repair-strategy';
import { AdfSchemaValidator } from '@atlassian/adf-parser/schema-validator';
import { AdfToolCallsDebugger } from '@atlassian/editor-ai-common/utils/adf-tool-calls-debugger';
import type {
	MultiFormatContentType,
	AiStreamingOrchestratorPlugin,
	StreamingCommand,
	StreamingError,
} from '@atlassian/editor-plugin-ai-streaming-orchestrator/types';
import type { AIPlugin } from '@atlassian/editor-plugin-ai/aiPluginType';
import { FAILURE_REASON } from '@atlassian/editor-plugin-ai/errors';
import type { AgentAttribution, AgentInfo } from '@atlassian/editor-plugin-ai/types';
import { getMauiIdFromAdfNode } from '@atlassian/malleable-ui-components/editor-remix/remix-ids';
import type { InsertedContentIdentity } from '@atlassian/malleable-ui-components/types';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import type { CwrRemixInvokedFrom } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';
import { InlineInputBoxAbortMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-abort-message-command';
import type { RovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/bridge-api';
import type { ClientId } from '@atlassian/rovo-content-bridge-api/client-types';

import type { AbortOptions, AbortReason } from '../types/abort-reason';
import { toSessionAbortReason } from '../types/abort-reason';
import type { ActionMetadata, ContentLocation } from '../types/content';
import {
	getCwrRemixResultInstrumentationId,
	type CwrRemixReplacement,
	type CwrRemixResult,
	type CwrRemixVisualMetadata,
	type OnCwrRemixReplacementCallback,
	type OnCwrRemixResultAppliedCallback,
} from '../types/cwr-remix-result';
import { log } from '../utils/aifc-logging';
import {
	fireInfiniteLoopDetectedAnalyticsEvent,
	fireParallelToolCallForceFinishedAnalyticsEvent,
	fireStreamingContractViolationAnalyticsEvent,
	fireUnsupportedToolCallAnalyticsEvent,
} from '../utils/analytics';
import { findCaptionReplacementTarget, isCaptionReplacementSnapshot } from './caption-replacement';
import { extractCwrRemixResults } from './extract-cwr-remix-results';
import {
	FinishAction,
	InitAction,
	isStreamable,
	PartialStreamParser,
	StreamAction,
} from './partial-stream-parser';
import {
	getEnvelopeContentType,
	PartialStreamParserV2,
	type ToolCallV2Envelope,
} from './partial-stream-parser-v2';
import {
	getRemixResultTargets,
	selectLastRemixResult,
	type RemixResultTarget,
} from './remix-result-selection';
import { ToolCallProcessorV2 } from './tool-call-processor-v2';
import {
	createToolProcessorChunkStrategy,
	type ToolProcessorChunkStrategy,
} from './tool-processor-chunk-strategies';

/** Default queue timeout (ms) when the Statsig dynamic config is unavailable. */
const DEFAULT_PARALLEL_QUEUE_TIMEOUT_MS = 2000;
const MAX_PENDING_CWR_REMIX_METADATA_STREAMS = 20;

type PreviousCwrRemixIdentity = {
	identity: InsertedContentIdentity;
	localId: string;
};

const toCwrRemixReplacement = (previous: PreviousCwrRemixIdentity): CwrRemixReplacement => ({
	previousLocalId: previous.localId,
	previousResultId: previous.identity.value,
});

const cwrRemixReplacementKey = (replacement: CwrRemixReplacement): string =>
	`${replacement.previousLocalId}:${replacement.previousResultId}`;

const cwrVisualIdentityKey = (
	identity: Pick<InsertedContentIdentity, 'attribute' | 'value'>,
): string => `${identity.attribute}:${identity.value}`;

function isPositiveNumber(value: unknown): value is number {
	return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/** Statsig dynamic-config name that holds tunable parallel-tool-call parameters. */
const QUEUE_CONFIG_NAME = 'platform_editor_ai_parallel_tool_calls_config';

/**
 * Reads `queueTimeoutMs` from the dedicated Statsig dynamic config. Falls back to
 * `DEFAULT_PARALLEL_QUEUE_TIMEOUT_MS` when the client is not initialized, the
 * config / parameter is missing, or the value is not a positive finite number.
 */
export function getParallelQueueTimeoutMs(): number {
	try {
		if (!FeatureGates.initializeCompleted()) {
			return DEFAULT_PARALLEL_QUEUE_TIMEOUT_MS;
		}
		const dynamicConfig = FeatureGates.getExperiment(QUEUE_CONFIG_NAME);
		const queueTimeoutMs = dynamicConfig?.value?.value;
		return isPositiveNumber(queueTimeoutMs) ? queueTimeoutMs : DEFAULT_PARALLEL_QUEUE_TIMEOUT_MS;
	} catch {
		return DEFAULT_PARALLEL_QUEUE_TIMEOUT_MS;
	}
}

/**
 * The total amount of times we're happy to process the same toolcall command. If more sequential toolcalls exist then
 * what this value is set too, then we have detected a possible infinite loop and we will abort the stream.
 */
const MAX_INFINITE_LOOP_COUNT = 5;
const SKIP_INFINITE_LOOP_ACTIONS = ['insertNodeAfter', 'insertNodeBefore'];

type EditorDocumentNode = {
	attrs: Record<string, unknown>;
	firstChild?: EditorDocumentNode | null;
	type: { name: string };
};

type EditorDocumentState = {
	doc: {
		descendants: (callback: (node: EditorDocumentNode) => boolean) => void;
	};
};

const hasEditorDocumentState = (value: unknown): value is EditorDocumentState => {
	if (typeof value !== 'object' || value === null || !('doc' in value)) {
		return false;
	}
	const document = value.doc;
	return (
		typeof document === 'object' &&
		document !== null &&
		'descendants' in document &&
		typeof document.descendants === 'function'
	);
};

class ToolCallProcessor {
	private actionId: string;
	private accumulated: string = '';
	private lastProcessedValue: string = '';
	private parser: AdfParser | undefined;
	private strategy: ToolProcessorChunkStrategy | undefined;
	private command: StreamingCommand;
	/**
	 * When true, initStream still runs on the first PART (normal lifecycle)
	 * but streamValue is held until finish() so the tool call lands in one write.
	 */
	private readonly atomicApply: boolean;

	constructor(
		private editorApi: PublicPluginAPI<AiStreamingOrchestratorPlugin>,
		private contentLocation: ContentLocation,
		command: StreamingCommand,
	) {
		this.atomicApply =
			isExperimentEnabled('platform_editor_ai_streaming_ux_experience_m1') &&
			isStreamable(command.type);
		this.command = command;

		const schema = this.editorApi.core.sharedState.currentState()?.schema;
		if (
			isExperimentEnabled('platform_editor_ai_multi_format_streaming') ||
			expValEquals('platform_editor_use_html_plus_parser', 'isEnabled', true)
		) {
			const contentType =
				'contentType' in this.command
					? (this.command.contentType as MultiFormatContentType | undefined)
					: undefined;
			this.strategy = createToolProcessorChunkStrategy(contentType, schema);
		} else if (schema) {
			this.parser = new AdfParser({
				schemaValidator: new AdfSchemaValidator(schema),
				repairStrategy: new AdfRepairStrategy(schema),
			});
		}

		this.actionId = this.initStreamingAction();
	}

	private initStreamingAction(): string {
		const actionId = this.editorApi?.aiStreamingOrchestrator?.actions.initAction(this.command, {
			/**
			 * Always hide the loading screen as staging area always shows Rovo conversation
			 * and will already have a loading indicator.
			 */
			hideLoadingScreen: this.contentLocation === 'staging-area',
			...(this.atomicApply ? { applyMode: 'commit' as const } : {}),
		});
		if (!actionId) {
			throw new Error('Failed to initialize streaming action for update');
		}
		log('Initialized editor streaming action', {
			streamType: 'update',
			actionId,
			command: this.command,
			atomicApply: this.atomicApply,
		});
		return actionId;
	}

	stream(chunk: string, isFinished: boolean) {
		if (this.atomicApply) {
			this.bufferChunk(chunk, isFinished);
			return;
		}

		if (!chunk) {
			// For replaceDoc, an empty value signals "clear the entire document".
			// We must still call streamValue so the streaming orchestrator can dispatch
			// the empty-paragraph fallback (instead of silently no-oping).
			if (this.command.type === 'replaceDoc') {
				this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId, '');
			}
			return;
		}

		this.dispatchProcessedChunk(chunk, isFinished, true);
	}

	/**
	 * Accumulate PART payloads without touching the orchestrator stream pacer.
	 */
	private bufferChunk(chunk: string, isFinished: boolean) {
		if (!chunk) {
			return;
		}
		this.dispatchProcessedChunk(chunk, isFinished, false);
	}

	private recordRawToolCallValue(rawValue: string): void {
		if (!isExperimentEnabled('platform_editor_ai_target_provenance')) {
			return;
		}

		const contentType = 'contentType' in this.command ? this.command.contentType : undefined;
		this.editorApi.aiStreamingOrchestrator?.actions.recordToolCallValue?.(
			this.actionId,
			rawValue,
			contentType ?? 'application/adf+json',
		);
	}

	private dispatchProcessedChunk(chunk: string, isFinished: boolean, emitStream: boolean) {
		if (
			isExperimentEnabled('platform_editor_ai_multi_format_streaming') ||
			expValEquals('platform_editor_use_html_plus_parser', 'isEnabled', true)
		) {
			const rawValue = this.accumulated + chunk;
			this.accumulated = rawValue;
			this.recordRawToolCallValue(rawValue);
			const processedAdf = this.strategy?.processChunk(chunk, isFinished) ?? chunk;
			this.lastProcessedValue = processedAdf;
			if (emitStream) {
				this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId, processedAdf);
				log('Streaming content', { actionId: this.actionId, chunk });
			}
			return;
		}

		if (this.parser) {
			const rawValue = this.accumulated + chunk;
			this.recordRawToolCallValue(rawValue);
			try {
				this.parser.processChunk(chunk, isFinished);
			} catch (error) {
				if (error instanceof AdfParseError) {
					// We want to ignore any parsing errors
				} else {
					// We still want to throw any other unexpected errors
					throw error;
				}
			}
			const processed = JSON.stringify(this.parser.getPartialDocument()) ?? '';
			this.lastProcessedValue = processed;
			if (emitStream) {
				this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId, processed);
			}
		} else {
			// Non-parser path: typewriter streams deltas; atomic apply needs the full buffer.
			const rawValue = this.accumulated + chunk;
			this.accumulated = rawValue;
			this.recordRawToolCallValue(rawValue);
			this.lastProcessedValue = this.accumulated;
			if (emitStream) {
				this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId, chunk);
				log('Streaming content', { actionId: this.actionId, chunk });
			}
			return;
		}

		this.accumulated += chunk;
		if (emitStream) {
			log('Streaming content', { actionId: this.actionId, chunk });
		}
	}

	finish() {
		if (this.atomicApply) {
			const valueToApply =
				this.lastProcessedValue ||
				(this.command.type === 'replaceDoc' && this.accumulated === '' ? '' : this.accumulated);
			// One write for the whole tool call, then the usual finishAction.
			if (valueToApply !== '' || this.command.type === 'replaceDoc') {
				this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId, valueToApply);
			}
		} else if (this.command.type === 'replaceDoc' && this.accumulated === '') {
			// For replaceDoc with an empty value, no StreamAction is emitted by the parser
			// (the "value" string is empty so `valueEnd.partial.length > 0` is never true).
			// We must call streamValue with an empty string so the orchestrator receives at
			// least one stream call and can apply its empty-paragraph fallback instead of
			// silently no-oping.
			this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId, '');
		}
		this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId);
		log('Finished action', {
			actionId: this.actionId,
			fullAdf: this.accumulated,
			atomicApply: this.atomicApply,
		});
		this.accumulated = '';
		this.lastProcessedValue = '';
	}
}

function assertUnreachable(_x: never) {}

/** A page edit's backend visual block counts from its final payload, passed on when the session ends. */
export type FinalVisualBlockCounts = {
	singleInstrumentationId: string;
	visualBlockCounts: AIResultVisualBlockCounts;
};

export class DocumentUpdateCommandProcessor {
	/**
	 * v1 parser. Always owned: it lives inside `parserV2` and the wrapper
	 * forwards non-streamable tool calls to it so behaviour for `removeNode`,
	 * `setAttrs`, `moveNode`, `wrap`, etc. is preserved bit-for-bit.
	 */
	private parser: PartialStreamParser;
	/**
	 * v2 hybrid parser. Routes streamable tool calls through the v2 envelope
	 * path and forwards non-streamable ones to the embedded v1 parser.
	 */
	private parserV2: PartialStreamParserV2;
	// --- FIFO parallel-queue state (used only when gate is ON) ---
	/** All distinct ids seen so far, in FIFO arrival order. */
	private v2OrderedIds: string[] = [];
	/** Latest envelope snapshot per queued (non-active) id. */
	private v2PendingSnapshotById: Map<string, ToolCallV2Envelope> = new Map();
	/** Per-id processor instances (active + already-finished ones still draining). */
	private v2ProcessorById: Map<string, ToolCallProcessorV2> = new Map();
	/** The id currently being actively applied. */
	private v2CurrentActiveId: string | undefined;
	/** Timeout handle for force-finishing a stalled active id. */
	private v2QueueTimeout: ReturnType<typeof setTimeout> | null = null;
	/** Latest observed `name` per envelope id (used for force-finished analytics). */
	private v2ToolCallNameById: Map<string, string> = new Map();
	/**
	 * Latest envelope snapshot received for an id after it was force-finished by the
	 * parallel-queue timeout. v2 envelopes are full snapshots, so the latest wins.
	 * Retained (not dropped) so {@link finish} can resume the tool call and avoid
	 * losing chunks that arrive post-timeout.
	 */
	private v2LateSnapshotByForceFinishedId: Map<string, ToolCallV2Envelope> = new Map();
	/** Command for each force-finished id, captured at force-finish time so the tool call can be resumed. */
	private v2CommandByForceFinishedId: Map<string, StreamingCommand> = new Map();
	/** Per-id tracking for force-finished ids; flushed as analytics on stream end. */
	private v2ForceFinishedRecords: Map<
		string,
		{
			/** Retain caption context if another command changes or removes the target. */
			captionReplacementTargeted: boolean;
			/** True if a `status: 'complete'` envelope for this id arrived after the force-finish. */
			completedAfterTimeout: boolean;
			/** Delay (ms) between force-finish and the first late chunk, or null if none arrived. */
			firstLateChunkDelayMs: number | null;
			/** `Date.now()` timestamp (ms since epoch) at which the force-finish occurred. */
			forceFinishedAt: number;
			/** Delay (ms) between force-finish and the most recent late chunk, or null if none arrived. */
			lastLateChunkDelayMs: number | null;
			/** Count of envelopes for this id that arrived after the force-finish. */
			lateChunkCount: number;
			/** The timeout value (ms) that was applied when this id was force-finished. */
			timeoutMs: number;
			/** The `name` field from the originating v2 envelope at force-finish time. */
			toolCallName: string;
		}
	> = new Map();
	/**
	 * Ids whose v2 processor latched `initFailed` and were dropped from the FIFO
	 * queue. Terminal for the id: lets us silently discard late/duplicate
	 * envelopes so we don't re-queue or re-fire the analytics event.
	 */
	private v2InitFailedIds: Set<string> = new Set();
	/**
	 * Tracks whether the per-session `htmleditor.streaming_contract_resolved`
	 * event has been fired yet. Spec §6.2: fire exactly once per session on
	 * first observed chunk.
	 */
	private streamingContractResolved: boolean = false;
	private currentToolCall: ToolCallProcessor | undefined;
	private currentToolCallName: string | null = null;
	private currentToolCallLocalId: string | null = null;
	private currentToolCallPreviousCwrRemixIdentity: PreviousCwrRemixIdentity | undefined;
	private currentToolCallCwrVisualIdentityKeys: Set<string> = new Set();
	private currentRawCommand: Record<string, unknown> | null = null;
	private currentStreamedValue: string = '';
	private accumulated: string = '';
	private lastAction: string | null;
	private hasDetectedInfiniteLoop: boolean = false;
	private loopsDetected: number = 0;
	private debugger: AdfToolCallsDebugger | null = null;
	private actionMetadata: ActionMetadata | null = null;
	private cwrRemixResults: CwrRemixResult[] = [];
	private cwrRemixSingleInstrumentationId: string | undefined;
	private cwrRemixInvokedFrom: CwrRemixInvokedFrom | undefined;
	private cwrRemixVisualMetadataByInstrumentationId: Map<
		string,
		Map<string, CwrRemixVisualMetadata>
	> = new Map();
	private pendingCwrRemixReplacements: Map<string, CwrRemixReplacement> = new Map();
	private v2PreviousCwrRemixIdentityByToolCallId: Map<
		string,
		PreviousCwrRemixIdentity | undefined
	> = new Map();
	private v2CwrVisualIdentityKeysByToolCallId: Map<string, Set<string>> = new Map();
	/**
	 * Whether a streaming session was opened for the current stream.
	 *
	 * The session is opened lazily on the first non-final chunk. Backend-persisted
	 * edits stream no chunks at all, so no session exists to close and
	 * `endSession()` would throw. See `finish()`.
	 */
	private hasOpenSession: boolean = false;
	private remixResultTargets: RemixResultTarget[] = [];

	constructor(
		private editorApi: PublicPluginAPI<[AIPlugin, AnalyticsPlugin, AiStreamingOrchestratorPlugin]>,
		private contentLocation: ContentLocation,
		private clientId: ClientId,
		private rovoBridgeAPI: RovoContentBridgeAPI,
		private onCwrRemixResultApplied: OnCwrRemixResultAppliedCallback | undefined = undefined,
		private onCwrRemixReplacementStarted: OnCwrRemixReplacementCallback | undefined = undefined,
		private onCwrRemixReplacementCancelled: OnCwrRemixReplacementCallback | undefined = undefined,
	) {
		this.parser = new PartialStreamParser();
		// Share the same v1 parser instance so that test mocks set on
		// `this.parser` still observe the v1 fall-through (e.g. when v1
		// tool calls are forwarded by the v2 wrapper).
		this.parserV2 = new PartialStreamParserV2(this.parser, (reason, metadata) =>
			fireStreamingContractViolationAnalyticsEvent(this.editorApi, reason, metadata),
		);
		this.lastAction = null;
		this.debugger = AdfToolCallsDebugger.getInstance();
	}

	/**
	 * Reset all per-session state.
	 *
	 * If `error` is provided, this is an error-teardown reset (called from
	 * `abort()` or the infinite-loop branch of `finish()`). In that case we
	 * propagate the error through `activeV2Processor.finish(error)` so the
	 * orchestrator dispatches an error event rather than treating the
	 * last-seen partial v2 snapshot as a successful tool-call result. Mirrors
	 * how the v1 path calls `finishAction(actionId, error)` via the queue's
	 * `endStreamCommand`.
	 */
	private reset(error?: StreamingError) {
		this.cancelPendingCwrRemixReplacements();
		this.remixResultTargets = [];
		this.accumulated = '';
		this.hasOpenSession = false;
		this.lastAction = null;
		this.currentToolCall = undefined;
		this.currentToolCallName = null;
		this.currentToolCallLocalId = null;
		this.currentToolCallPreviousCwrRemixIdentity = undefined;
		this.currentToolCallCwrVisualIdentityKeys = new Set();
		this.currentRawCommand = null;
		this.currentStreamedValue = '';
		this.cwrRemixResults = [];
		this.cwrRemixSingleInstrumentationId = undefined;
		this.cwrRemixInvokedFrom = undefined;
		this.v2PreviousCwrRemixIdentityByToolCallId.clear();
		this.v2CwrVisualIdentityKeysByToolCallId.clear();
		this.hasDetectedInfiniteLoop = false;
		this.loopsDetected = 0;
		this.parser.reset();
		this.parserV2.reset();
		// Finish any leftover v2 processor first so it emits its final
		// `finishAction` to the orchestrator. Propagate `error` so the
		// orchestrator does NOT commit the last partial snapshot to the
		// document as a successful result on abort / error teardown.
		// Reset the FIFO parallel-queue state (finishes any in-flight
		// processors that belong to the new scheduler path).
		this.resetParallelQueueState(error);
		this.streamingContractResolved = false;
	}

	private beginCwrRemixReplacement(previous: PreviousCwrRemixIdentity | undefined): void {
		// Deletion suppression must start with the first partial update. It is independent
		// of whether this stream has an analytics ID for the eventual result.
		if (!previous) {
			return;
		}

		const replacement = toCwrRemixReplacement(previous);
		this.pendingCwrRemixReplacements.set(cwrRemixReplacementKey(replacement), replacement);
		this.onCwrRemixReplacementStarted?.(replacement);
	}

	private cancelCwrRemixReplacement(previous: PreviousCwrRemixIdentity | undefined): void {
		if (!previous) {
			return;
		}

		const replacement = toCwrRemixReplacement(previous);
		if (this.pendingCwrRemixReplacements.delete(cwrRemixReplacementKey(replacement))) {
			this.onCwrRemixReplacementCancelled?.(replacement);
		}
	}

	private cancelPendingCwrRemixReplacements(): void {
		this.pendingCwrRemixReplacements.forEach((replacement) =>
			this.onCwrRemixReplacementCancelled?.(replacement),
		);
		this.pendingCwrRemixReplacements.clear();
	}

	/** Records the semantic type emitted before the matching ADF update reaches the editor. */
	recordCwrRemixVisualMetadata(
		singleInstrumentationId: string,
		resultId: string,
		metadata: CwrRemixVisualMetadata,
	): void {
		if (!isExperimentEnabled('platform_editor_cwr_remix_acceptance')) {
			return;
		}

		let metadataByResultId =
			this.cwrRemixVisualMetadataByInstrumentationId.get(singleInstrumentationId);
		if (!metadataByResultId) {
			if (
				this.cwrRemixVisualMetadataByInstrumentationId.size >=
				MAX_PENDING_CWR_REMIX_METADATA_STREAMS
			) {
				const oldestInstrumentationId = this.cwrRemixVisualMetadataByInstrumentationId
					.keys()
					.next().value;
				if (oldestInstrumentationId) {
					this.cwrRemixVisualMetadataByInstrumentationId.delete(oldestInstrumentationId);
				}
			}
			metadataByResultId = new Map();
		}
		metadataByResultId.set(resultId, metadata);
		this.cwrRemixVisualMetadataByInstrumentationId.set(singleInstrumentationId, metadataByResultId);
	}

	private getCwrRemixVisualMetadata(): ReadonlyMap<string, CwrRemixVisualMetadata> | undefined {
		return this.cwrRemixSingleInstrumentationId
			? this.cwrRemixVisualMetadataByInstrumentationId.get(this.cwrRemixSingleInstrumentationId)
			: undefined;
	}

	private createToolCallProcessor(toolCall: InitAction) {
		const type = toolCall.name;
		const localId = toolCall.localId ?? '';
		const contentType = toolCall.contentType ?? undefined;

		let command: StreamingCommand;
		switch (type) {
			case 'replaceDoc':
				// replaceDoc has no localId — it replaces the entire document.
				// contentType is 'text/html' or 'application/adf+json' (enforced by the parser).
				command = {
					type,
					contentType,
					...(toolCall.noStream && { noStream: true }),
				};
				break;
			case 'replaceNode':
			case 'insertNodeBefore':
			case 'insertNodeAfter':
				command = {
					type,
					localId,
					contentType,
					...(toolCall.noStream && { noStream: true }),
				};
				break;
			case 'removeNode':
				command = { type, localId };
				break;
			case 'removeNodes':
				const localIds = toolCall.localIds;
				if (!localIds) {
					return;
				}
				command = { type, localIds };
				break;
			case 'moveNode':
				const anchorLocalId = toolCall.anchorLocalId;
				const relationship = toolCall.relationship;
				if (!localId || !anchorLocalId || !relationship) {
					return;
				}

				command = { type, localId, anchorLocalId, relationship };
				break;
			case 'appendNode':
			case 'appendNodeToEnd':
				command = { type, contentType };
				break;
			case 'wrap':
			case 'wrapNode':
				const wrapperType = toolCall.wrapperType;
				const wrapperAttrs = toolCall.wrapperAttrs;
				if (!wrapperType || !wrapperAttrs) {
					return;
				}

				command = { type, localId, wrapperType, wrapperAttrs };
				break;
			case 'setAttrs':
				const attrs = toolCall.attrs;
				if (!attrs) {
					return;
				}

				command = { type, localId, attrs };
				break;

			default:
				assertUnreachable(type);
				return;
		}

		return new ToolCallProcessor(this.editorApi, this.contentLocation, command);
	}

	streamChunk(
		chunk: string,
		isFinished: boolean,
		traceId?: string,
		singleInstrumentationId?: string,
		agentInfo?: AgentInfo,
		// True when the originating Rovo-chat edit was an inline edit
		// (`experience === 'inline_edit'`). Forwarded to the orchestrator so the
		// Post Stream Review "Review moment" auto-opens as the editor modal.
		isInlineEditSession?: boolean,
		agentAttribution?: AgentAttribution,
		// True when the originating edit was from Rovo chat (`chat_edit` or
		// Create-with-Rovo). Forwarded so the Review moment can auto-open
		// headless (controls in chat, no editor modal).
		isChatEditSession?: boolean,
		cwrRemixInvokedFrom?: CwrRemixInvokedFrom,
	): void {
		// The last message contains a copy of the full, accumulated tool call structure. We
		// don't want to reprocess this, so we just return instead.
		if (isFinished) {
			if (fg('aifc_edit_maui')) {
				const schema = this.editorApi.core.sharedState.currentState()?.schema;
				this.remixResultTargets = schema ? getRemixResultTargets(chunk, schema) : [];
			}
			return;
		}

		if (this.accumulated.length === 0) {
			this.reset();
			// Sender and receiver can deploy independently. Do not enable CWR tracking from
			// its optional wire attribute unless this client is in the experiment.
			this.cwrRemixInvokedFrom =
				cwrRemixInvokedFrom && isExperimentEnabled('platform_editor_cwr_remix_acceptance')
					? cwrRemixInvokedFrom
					: undefined;
			this.cwrRemixSingleInstrumentationId = singleInstrumentationId;
			this.editorApi.aiStreamingOrchestrator?.actions.startSession(traceId, {
				singleInstrumentationId,
				promptVersion: fg('platform_editor_ai_promptversion_in_analytics')
					? this.actionMetadata?.promptVersion
					: undefined,
				agentInfo: editorExperiment('platform_editor_agent_mentions', true) ? agentInfo : undefined,
				isInlineEditSession,
				isChatEditSession,
				agentAttribution,
			});
			this.debugger?.startSession(traceId, 'update');
			this.hasOpenSession = true;
		}

		this.accumulated += chunk;
		return this.stream(chunk, isFinished);
	}

	private stream(chunk: string, isFinished: boolean) {
		// Once we detect an infinite loop and call finish(), we won't be able to recover
		if (this.hasDetectedInfiniteLoop) {
			return;
		}

		// V2 path: feed the hybrid parser when the chunk looks like it might
		// carry v2 provisional metadata. The primary discriminator per spec
		// §1.4 is the presence of a top-level `"id"` inside a `toolCalls`
		// object; the substring check below is a fast pre-filter to avoid the
		// JSON-array parse work on the vast majority of chunks that are
		// clearly mid-v1-array.
		let results: ReturnType<PartialStreamParser['parse']>;
		const looksLikeV2 = chunk.includes('"id"');
		if (looksLikeV2) {
			results = this.parserV2.feed(chunk);
			const envelopes = this.parserV2.drainV2Envelopes();
			for (const envelope of envelopes) {
				this.capturePreviousCwrRemixIdentityForV2Envelope(envelope);
				this.collectCwrRemixResultsFromV2Envelope(envelope);
				// Spec §6.2: fire `streaming_contract_resolved` exactly once per
				// session, on first observed v2 chunk. Include
				// `streamingContract: 'v2'` for uniform queryability alongside
				// the other v2 analytics events (`v2_envelope_malformed`,
				// `v2_stale_sequence`, `partial_after_complete`).
				if (!this.streamingContractResolved) {
					this.streamingContractResolved = true;
					log('htmleditor.streaming_contract_resolved', {
						streamingContract: 'v2',
						feContractVersion: 2,
						resolvedV2: true,
					});
				}

				// Force-finish is terminal for an id: record late chunks and discard.
				if (this.v2ForceFinishedRecords.has(envelope.id)) {
					this.recordLateChunkIfForceFinished(envelope);
					continue;
				}
				// Init-failure is terminal for an id too: silently discard any
				// late/duplicate envelope so we don't re-queue or re-fire analytics.
				if (this.v2InitFailedIds.has(envelope.id)) {
					continue;
				}
				this.v2ToolCallNameById.set(envelope.id, envelope.name);
				if (!this.v2OrderedIds.includes(envelope.id)) {
					this.v2OrderedIds.push(envelope.id);
				}
				if (!this.v2CurrentActiveId) {
					this.v2CurrentActiveId = envelope.id;
				}
				if (envelope.id === this.v2CurrentActiveId) {
					this.applyActiveV2Envelope(envelope);
				} else {
					// Queue latest snapshot per id; v2 envelopes are full snapshots.
					this.v2PendingSnapshotById.set(envelope.id, envelope);
					this.armParallelQueueTimeout();
				}
			}
		} else {
			results = this.parser.parse(chunk);
		}

		for (const result of results) {
			if (result instanceof InitAction) {
				if (result.localId) {
					if (
						this.lastAction === `${result.name}-${result.localId}` &&
						!SKIP_INFINITE_LOOP_ACTIONS.includes(result.name)
					) {
						this.loopsDetected++;
						if (this.loopsDetected >= MAX_INFINITE_LOOP_COUNT) {
							this.hasDetectedInfiniteLoop = true;
							fireInfiniteLoopDetectedAnalyticsEvent(this.editorApi, result);
							this.debugger?.recordSessionError(
								'infinite_loop_detected',
								`Infinite loop detected for ${result.name} on localId ${result.localId}`,
							);
							return;
						} else {
							this.initToolCallWithDebugger(result);
						}
					} else {
						this.loopsDetected = 0;
						this.initToolCallWithDebugger(result);
					}
					this.lastAction = `${result.name}-${result.localId}`;
				} else {
					this.lastAction = null;
					this.initToolCallWithDebugger(result);
				}
			} else if (result instanceof StreamAction) {
				this.currentToolCall?.stream(result.partialValue, isFinished);
				this.currentStreamedValue += result.partialValue;
			} else if (result instanceof FinishAction) {
				this.currentToolCall?.finish();
				if (this.cwrRemixInvokedFrom && this.currentToolCallName && this.currentStreamedValue) {
					const cwrRemixResults = this.filterNewCwrRemixResults(
						extractCwrRemixResults(
							this.currentToolCallName,
							this.currentStreamedValue,
							this.getCwrRemixVisualMetadata(),
						),
						this.currentToolCallCwrVisualIdentityKeys,
					);
					const resultsWithPrevious = this.withPreviousCwrRemixResult(
						cwrRemixResults,
						this.currentToolCallPreviousCwrRemixIdentity,
					);
					this.cwrRemixResults.push(...resultsWithPrevious);
					if (!resultsWithPrevious.some((result) => result.previousResultId)) {
						this.cancelCwrRemixReplacement(this.currentToolCallPreviousCwrRemixIdentity);
					}
				} else {
					this.cancelCwrRemixReplacement(this.currentToolCallPreviousCwrRemixIdentity);
				}
				if (this.currentToolCallName) {
					const fullRawCommand = this.currentRawCommand
						? {
								...this.currentRawCommand,
								...(this.currentStreamedValue ? { value: this.currentStreamedValue } : {}),
							}
						: null;
					this.debugger?.recordToolCallSuccess(
						this.currentToolCallName,
						this.currentToolCallLocalId,
						fullRawCommand,
					);
				}
				this.currentToolCallName = null;
				this.currentToolCallLocalId = null;
				this.currentToolCallPreviousCwrRemixIdentity = undefined;
				this.currentToolCallCwrVisualIdentityKeys = new Set();
				this.currentRawCommand = null;
				this.currentStreamedValue = '';
			} else {
				throw new Error('Unknown action type from partial stream parser');
			}
		}
	}

	// ---------------------------------------------------------------------------
	// FIFO parallel-queue helpers (used only when gate is ON)
	// ---------------------------------------------------------------------------

	/**
	 * Apply one v2 envelope for the currently-active tool-call id.
	 * Creates a processor on first use; on `status === 'complete'` advances
	 * to the next queued id.
	 */
	private applyActiveV2Envelope(envelope: ToolCallV2Envelope): void {
		let processor = this.v2ProcessorById.get(envelope.id);
		if (!processor) {
			processor = new ToolCallProcessorV2(this.editorApi, this.contentLocation);
			processor.bindEnvelopeId(envelope.id);
			this.v2ProcessorById.set(envelope.id, processor);
		}
		processor.apply(envelope);

		// If the processor latched `initFailed` (unsupported name or missing
		// arguments) it will never call `finishAction`. Release the FIFO slot,
		// advance to the next queued id, and fire an analytics event to monitor
		// these drops.
		if (processor.getInitFailed()) {
			fireUnsupportedToolCallAnalyticsEvent(this.editorApi, {
				envelopeId: envelope.id,
				toolCallName: this.v2ToolCallNameById.get(envelope.id) ?? envelope.name,
			});
			// Mark the id terminal so any late/duplicate envelope is discarded
			// rather than re-queued and re-fired.
			this.v2InitFailedIds.add(envelope.id);
			this.v2ProcessorById.delete(envelope.id);
			this.advanceToNextV2();
			return;
		}

		if (envelope.status === 'complete') {
			this.v2ProcessorById.delete(envelope.id);
			this.advanceToNextV2();
		}
	}

	/**
	 * Called when the currently-active id finishes (or is force-finished by
	 * the timeout). Removes the id from the FIFO queue and starts the next
	 * queued id if one is waiting.
	 */
	private advanceToNextV2(): void {
		this.clearParallelQueueTimeout();
		this.v2OrderedIds = this.v2OrderedIds.filter((id) => id !== this.v2CurrentActiveId);
		this.v2CurrentActiveId = undefined;

		const nextId = this.v2OrderedIds.find((id) => this.v2PendingSnapshotById.has(id));
		if (!nextId) {
			return;
		}
		this.v2CurrentActiveId = nextId;
		const snapshot = this.v2PendingSnapshotById.get(nextId);
		this.v2PendingSnapshotById.delete(nextId);
		if (!snapshot) {
			// Defensive: should not happen because we found `nextId` via `.has()`,
			// but if it does, skip this id and try the next one rather than stalling.
			this.advanceToNextV2();
			return;
		}
		this.applyActiveV2Envelope(snapshot);

		// Re-arm only while other ids are still queued behind this one.
		const queueStillHasOthers = this.v2OrderedIds.some(
			(id) => id !== this.v2CurrentActiveId && this.v2PendingSnapshotById.has(id),
		);
		if (queueStillHasOthers) {
			this.armParallelQueueTimeout();
		}
	}

	/** Arms a one-shot timeout that force-finishes the active id if it stalls. */
	private armParallelQueueTimeout(): void {
		if (this.v2QueueTimeout) {
			return;
		}
		const timeoutMs = getParallelQueueTimeoutMs();
		this.v2QueueTimeout = setTimeout(() => {
			this.v2QueueTimeout = null;
			const forcedId = this.v2CurrentActiveId;
			const activeProcessor = forcedId ? this.v2ProcessorById.get(forcedId) : undefined;

			// Skip force-finish if no other id is waiting on the queue.
			const hasPendingOtherIds = this.v2OrderedIds.some(
				(id) => id !== forcedId && this.v2PendingSnapshotById.has(id),
			);
			if (!hasPendingOtherIds) {
				return;
			}

			if (activeProcessor && forcedId) {
				// Timeout force-finish: capture the command and flag the finish so the
				// orchestrator records this tool call's working positions before the
				// experience ends. It is resumed at its mapped position once streaming
				// finishes (see resumeAbortedToolCalls).
				const forcedCommand = activeProcessor.getCommand();
				if (forcedCommand) {
					this.v2CommandByForceFinishedId.set(forcedId, forcedCommand);
				}
				activeProcessor.finish(undefined, { abortedDueToTimeout: true });
				this.v2ProcessorById.delete(forcedId);
				if (!this.v2ForceFinishedRecords.has(forcedId)) {
					this.v2ForceFinishedRecords.set(forcedId, {
						captionReplacementTargeted: activeProcessor.hasCaptionReplacementTarget(),
						timeoutMs,
						forceFinishedAt: Date.now(),
						lateChunkCount: 0,
						firstLateChunkDelayMs: null,
						lastLateChunkDelayMs: null,
						completedAfterTimeout: false,
						toolCallName: this.v2ToolCallNameById.get(forcedId) ?? '',
					});
				}
				log('htmleditor.parallel_tool_call.force_finished', {
					streamingContract: 'v2',
					envelopeId: forcedId,
					reason: 'queue_timeout',
					timeoutMs,
				});
			}
			this.advanceToNextV2();
		}, timeoutMs);
	}

	/** Updates late-chunk counters for a previously force-finished id. */
	private recordLateChunkIfForceFinished(envelope: ToolCallV2Envelope): void {
		const record = this.v2ForceFinishedRecords.get(envelope.id);
		if (!record) {
			return;
		}
		const rawValue = envelope.arguments?.value;
		if (
			typeof rawValue === 'string' &&
			isExperimentEnabled('platform_editor_ai_target_provenance')
		) {
			this.editorApi.aiStreamingOrchestrator?.actions.recordToolCallValue?.(
				envelope.id,
				rawValue,
				getEnvelopeContentType(envelope),
			);
		}
		const delayMs = Date.now() - record.forceFinishedAt;
		record.lateChunkCount += 1;
		if (record.firstLateChunkDelayMs === null) {
			record.firstLateChunkDelayMs = delayMs;
		}
		record.lastLateChunkDelayMs = delayMs;
		if (envelope.status === 'complete') {
			record.completedAfterTimeout = true;
		}
		// Retain the latest late snapshot so the aborted tool call can be resumed at
		// stream end instead of lost. Full snapshots, so the latest supersedes.
		const previous = this.v2LateSnapshotByForceFinishedId.get(envelope.id);
		const command = this.v2CommandByForceFinishedId.get(envelope.id);
		let snapshot = envelope;
		if (
			envelope.arguments?.value === undefined &&
			typeof previous?.arguments?.value === 'string' &&
			command?.type === 'replaceNode' &&
			isExperimentEnabled('platform_editor_ai_caption_replacement')
		) {
			if (!record.captionReplacementTargeted) {
				this.editorApi.core.actions?.execute(({ tr }) => {
					record.captionReplacementTargeted = !!findCaptionReplacementTarget(
						tr.doc,
						command.localId,
					);
					return null;
				});
			}
			if (
				record.captionReplacementTargeted ||
				isCaptionReplacementSnapshot(previous.arguments.value)
			) {
				// An envelope may contain only lifecycle metadata. Keep the latest full
				// caption snapshot for validation, including snapshots that must reject.
				snapshot = {
					...envelope,
					arguments: { ...envelope.arguments, value: previous.arguments.value },
				};
			}
		}
		this.v2LateSnapshotByForceFinishedId.set(envelope.id, snapshot);
	}

	/** Clears any armed parallel-queue timeout. */
	private clearParallelQueueTimeout(): void {
		if (this.v2QueueTimeout) {
			clearTimeout(this.v2QueueTimeout);
			this.v2QueueTimeout = null;
		}
	}

	/** Reset all FIFO parallel-queue state fields. */
	private resetParallelQueueState(error?: StreamingError): void {
		this.clearParallelQueueTimeout();
		for (const processor of this.v2ProcessorById.values()) {
			processor.finish(error);
		}
		this.v2ProcessorById.clear();
		this.v2PendingSnapshotById.clear();
		this.v2OrderedIds = [];
		this.v2CurrentActiveId = undefined;
		// Drop force-finished records on abort; `finish()` flushes them first.
		this.v2ForceFinishedRecords.clear();
		// Drop retained snapshots/commands; `finish()` resumes them before reset.
		this.v2LateSnapshotByForceFinishedId.clear();
		this.v2CommandByForceFinishedId.clear();
		this.v2InitFailedIds.clear();
		this.v2ToolCallNameById.clear();
	}

	/**
	 * Resume tool calls force-finished by the queue timeout that later received
	 * envelopes. Re-applies each latest snapshot via `resumeAction` at the tool
	 * call's mapped position, in FIFO arrival order, within the still-open session.
	 * `resumeAction` reads the stored positions, streams the snapshot, finishes the
	 * action, and clears the positions.
	 */
	private resumeAbortedToolCalls(): void {
		if (this.v2LateSnapshotByForceFinishedId.size === 0) {
			return;
		}
		// Preserve original arrival order where known; fall back to insertion order.
		const orderedIds = [
			...this.v2OrderedIds.filter((id) => this.v2LateSnapshotByForceFinishedId.has(id)),
			...[...this.v2LateSnapshotByForceFinishedId.keys()].filter(
				(id) => !this.v2OrderedIds.includes(id),
			),
		];
		for (const id of orderedIds) {
			const snapshot = this.v2LateSnapshotByForceFinishedId.get(id);
			const command = this.v2CommandByForceFinishedId.get(id);
			if (!snapshot || !command) {
				continue;
			}
			// Skip resume if the snapshot has no string value: resuming with an empty
			// value would silently overwrite the aborted tool call's content.
			if (typeof snapshot.arguments?.value !== 'string') {
				continue;
			}
			const value = snapshot.arguments.value;
			const captionRecoveryEnabled = isExperimentEnabled('platform_editor_ai_caption_replacement');
			const requireCaptionTarget =
				captionRecoveryEnabled && !!this.v2ForceFinishedRecords.get(id)?.captionReplacementTargeted;
			if (
				command.type === 'replaceNode' &&
				(captionRecoveryEnabled ||
					isExperimentEnabled('platform_editor_ai_schema_rejection_preservation'))
			) {
				let target: ReturnType<typeof findCaptionReplacementTarget>;
				if (captionRecoveryEnabled && !requireCaptionTarget) {
					this.editorApi.core.actions?.execute(({ tr }) => {
						target = findCaptionReplacementTarget(tr.doc, command.localId);
						return null;
					});
				}
				if (requireCaptionTarget || target || isCaptionReplacementSnapshot(value)) {
					// Caption fragments need v2 validation on resume too. Resolve the current
					// target by localId, preserving its exact caption range when recoverable
					// and the schema-rejection protection otherwise.
					this.editorApi.aiExperience?.actions.clearAbortedToolCallPosition?.(id);
					const processor = new ToolCallProcessorV2(this.editorApi, this.contentLocation, {
						requireCaptionTarget,
					});
					processor.bindEnvelopeId(id);
					processor.apply({
						...snapshot,
						status: 'complete',
						name: command.type,
						arguments: {
							...snapshot.arguments,
							contentType: command.contentType,
							localId: command.localId,
							noStream: command.noStream,
						},
					});
					continue;
				}
			}
			this.editorApi.aiStreamingOrchestrator?.actions.resumeAction(id, command, value);
			log('htmleditor.parallel_tool_call.resumed_aborted_tool_call', {
				streamingContract: 'v2',
				envelopeId: id,
				status: snapshot.status,
			});
		}
		this.v2LateSnapshotByForceFinishedId.clear();
		this.v2CommandByForceFinishedId.clear();
	}

	/** Fires one `parallelToolCall.forceFinished` analytics event per force-finished id. */
	private flushForceFinishedAnalyticsEvents(): void {
		if (this.v2ForceFinishedRecords.size === 0) {
			return;
		}
		for (const [envelopeId, record] of this.v2ForceFinishedRecords) {
			fireParallelToolCallForceFinishedAnalyticsEvent(this.editorApi, {
				envelopeId,
				timeoutMs: record.timeoutMs,
				lateChunkCount: record.lateChunkCount,
				firstLateChunkDelayMs: record.firstLateChunkDelayMs,
				lastLateChunkDelayMs: record.lastLateChunkDelayMs,
				completedAfterTimeout: record.completedAfterTimeout,
				toolCallName: record.toolCallName,
			});
		}
		this.v2ForceFinishedRecords.clear();
	}

	private static buildRawCommand(result: InitAction): Record<string, unknown> {
		const raw: Record<string, unknown> = { name: result.name };
		if (result.localId != null) {
			raw.localId = result.localId;
		}
		if (result.anchorLocalId != null) {
			raw.anchorLocalId = result.anchorLocalId;
		}
		if (result.relationship != null) {
			raw.relationship = result.relationship;
		}
		if (result.contentType != null) {
			raw.contentType = result.contentType;
		}
		if (result.wrapperType != null) {
			raw.wrapperType = result.wrapperType;
		}
		if (result.wrapperAttrs != null) {
			raw.wrapperAttrs = result.wrapperAttrs;
		}
		if (result.attrs != null) {
			raw.attrs = result.attrs;
		}
		return raw;
	}

	private initToolCallWithDebugger(result: InitAction) {
		this.currentToolCallName = result.name;
		this.currentToolCallLocalId = result.localId ?? null;
		this.currentToolCallCwrVisualIdentityKeys = this.captureCwrVisualIdentityKeys();
		this.currentToolCallPreviousCwrRemixIdentity = this.capturePreviousCwrRemixIdentity(
			result.name,
			result.localId,
		);
		this.beginCwrRemixReplacement(this.currentToolCallPreviousCwrRemixIdentity);
		this.currentRawCommand = DocumentUpdateCommandProcessor.buildRawCommand(result);

		try {
			this.currentToolCall = this.createToolCallProcessor(result);
			if (!this.currentToolCall) {
				this.debugger?.recordToolCallError(
					result.name,
					result.localId ?? null,
					'invalid_command_found',
					`Failed to create tool call processor for ${result.name}: missing required attributes`,
					this.currentRawCommand,
				);
			}
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);
			this.debugger?.recordToolCallError(
				result.name,
				result.localId ?? null,
				'tool_call_init_failed',
				message,
				this.currentRawCommand,
			);
		}
	}

	/** Capture the old visual before a CWR replacement can stream over its target. */
	private capturePreviousCwrRemixIdentity(
		operation: string,
		localId: string | null | undefined,
	): PreviousCwrRemixIdentity | undefined {
		if (!this.cwrRemixInvokedFrom || operation !== 'replaceNode' || !localId) {
			return undefined;
		}

		const editorState = this.editorApi.core.sharedState.currentState();
		if (!hasEditorDocumentState(editorState)) {
			return undefined;
		}

		let previousIdentity: PreviousCwrRemixIdentity | undefined;
		editorState.doc.descendants((node) => {
			if (node.attrs.localId !== localId) {
				return true;
			}

			if (node.type.name === 'extension' && node.attrs.extensionKey === 'native-embed:maui') {
				const mauiId = getMauiIdFromAdfNode(node);
				if (mauiId) {
					previousIdentity = {
						identity: {
							attribute: 'mauiVersion',
							localId,
							value: mauiId,
						},
						localId,
					};
				}
				return false;
			}

			const mediaNode = node.type.name === 'mediaSingle' ? node.firstChild : node;
			const mediaId = mediaNode?.attrs.id;
			if (
				mediaNode?.type.name === 'media' &&
				mediaNode.attrs.type === 'file' &&
				typeof mediaId === 'string'
			) {
				previousIdentity = { identity: { attribute: 'id', value: mediaId }, localId };
			}
			return false;
		});
		return previousIdentity;
	}

	/** Captures the document state immediately before one CWR operation is applied. */
	private captureCwrVisualIdentityKeys(): Set<string> {
		if (!this.cwrRemixInvokedFrom) {
			return new Set();
		}

		const editorState = this.editorApi.core.sharedState.currentState();
		if (!hasEditorDocumentState(editorState)) {
			return new Set();
		}

		const identities = new Set<string>();
		editorState.doc.descendants((node) => {
			if (node.type.name === 'extension' && node.attrs.extensionKey === 'native-embed:maui') {
				const mauiId = getMauiIdFromAdfNode(node);
				if (mauiId) {
					identities.add(cwrVisualIdentityKey({ attribute: 'mauiVersion', value: mauiId }));
				}
			}
			if (node.type.name === 'media' && typeof node.attrs.id === 'string') {
				identities.add(cwrVisualIdentityKey({ attribute: 'id', value: node.attrs.id }));
			}
			return true;
		});
		return identities;
	}

	private capturePreviousCwrRemixIdentityForV2Envelope(envelope: ToolCallV2Envelope): void {
		if (!this.cwrRemixInvokedFrom || this.v2PreviousCwrRemixIdentityByToolCallId.has(envelope.id)) {
			return;
		}

		const localId =
			typeof envelope.arguments.localId === 'string' ? envelope.arguments.localId : undefined;
		const previous = this.capturePreviousCwrRemixIdentity(envelope.name, localId);
		this.v2PreviousCwrRemixIdentityByToolCallId.set(envelope.id, previous);
		this.v2CwrVisualIdentityKeysByToolCallId.set(envelope.id, this.captureCwrVisualIdentityKeys());
		this.beginCwrRemixReplacement(previous);
	}

	private filterNewCwrRemixResults(
		results: CwrRemixResult[],
		previousIdentityKeys: Set<string>,
	): CwrRemixResult[] {
		return results.filter(
			(result) => !previousIdentityKeys.has(cwrVisualIdentityKey(result.identity)),
		);
	}

	private withPreviousCwrRemixResult(
		results: CwrRemixResult[],
		previous: PreviousCwrRemixIdentity | undefined,
	): CwrRemixResult[] {
		if (
			results.length !== 1 ||
			!previous ||
			results[0].identity.attribute !== previous.identity.attribute
		) {
			return results;
		}

		// A single same-kind replacement can be classified as an undo/revert later.
		return [
			{
				...results[0],
				previousLocalId: previous.localId,
				previousResultId: previous.identity.value,
			},
		];
	}

	async finish(finalVisualBlockCounts?: FinalVisualBlockCounts): Promise<void> {
		const remixResultTargets = this.remixResultTargets;
		if (this.hasDetectedInfiniteLoop) {
			this.rovoBridgeAPI.send(InlineInputBoxAbortMessageCommand, {
				receiverId: ROVO_CHAT_CLIENT_ID,
				senderId: this.clientId,
			});
			this.debugger?.endSession('errored');
			// Propagate an error to any in-flight v2 processor so its
			// `finishAction(actionId, error)` reflects the infinite-loop
			// teardown rather than committing the last partial snapshot as a
			// successful result.
			this.reset({
				failureReason: FAILURE_REASON.ADF_STREAMING_ERROR,
				errorKey: 'infinite_loop_detected',
			});
			await this.editorApi.aiStreamingOrchestrator?.actions.endSession();
		} else {
			this.debugger?.endSession('completed');
			this.accumulated = '';
			// Best-effort drain of any queued v2 snapshots.
			// Finish the active processor first (it already had apply() called; just needs finish()).
			const activeProc = this.v2CurrentActiveId
				? this.v2ProcessorById.get(this.v2CurrentActiveId)
				: undefined;
			if (activeProc && this.v2CurrentActiveId) {
				activeProc.finish();
				this.v2ProcessorById.delete(this.v2CurrentActiveId);
			}
			// Drain queued (non-active) snapshots — these haven't had apply() yet.
			for (const id of this.v2OrderedIds) {
				if (id === this.v2CurrentActiveId) {
					continue; // already finished above
				}
				const snapshot = this.v2PendingSnapshotById.get(id);
				if (snapshot) {
					const drainProcessor = new ToolCallProcessorV2(this.editorApi, this.contentLocation);
					drainProcessor.bindEnvelopeId(id);
					drainProcessor.apply(snapshot);
					if (snapshot.status !== 'complete') {
						drainProcessor.finish();
					}
				}
			}
			// Resume aborted tool calls: runs after active/queued processors and
			// before reset. Enqueues commands the queue drains while the session is
			// still open, so this must stay before endSession() below.
			this.resumeAbortedToolCalls();
			// Must run BEFORE reset (which clears the records map).
			this.flushForceFinishedAnalyticsEvents();
			this.resetParallelQueueState();
			this.streamingContractResolved = false;
			if (fg('platform_editor_ai_fix_update_backend_end_experience')) {
				if (this.hasOpenSession) {
					await this.editorApi.aiStreamingOrchestrator?.actions.endSession(
						undefined,
						finalVisualBlockCounts,
					);
					this.hasOpenSession = false;
				} else {
					// Backend-persisted edits complete without streaming any chunk, so no
					// session was ever opened. `endSession()` throws in that state, and the
					// resulting abort skips the completion callbacks that dismiss the inline
					// prompt. Tear the experience down directly instead: there is no
					// streamed content to accept, reject or review.
					this.editorApi.aiExperience?.actions.endAIExperience();
				}
			} else {
				await this.editorApi.aiStreamingOrchestrator?.actions.endSession(
					undefined,
					finalVisualBlockCounts,
				);
			}
			this.flushCwrRemixResults();
			if (
				fg('aifc_edit_maui') &&
				remixResultTargets.length > 0 &&
				this.remixResultTargets === remixResultTargets
			) {
				// Stream commits and experience teardown move the text cursor. Restore
				// asset selection only after they finish, and never after an abort/reset.
				this.editorApi.core.actions.execute(selectLastRemixResult(remixResultTargets));
				this.remixResultTargets = [];
			}
			this.lastAction = null;
			this.currentToolCall = undefined;
			this.currentToolCallName = null;
			this.currentToolCallLocalId = null;
			this.currentToolCallPreviousCwrRemixIdentity = undefined;
			this.currentToolCallCwrVisualIdentityKeys = new Set();
			this.currentRawCommand = null;
			this.currentStreamedValue = '';
			this.v2PreviousCwrRemixIdentityByToolCallId.clear();
			this.v2CwrVisualIdentityKeysByToolCallId.clear();
			this.parser.reset();
			this.parserV2.reset();
		}
	}

	private flushCwrRemixResults(): void {
		if (!this.cwrRemixInvokedFrom) {
			return;
		}

		for (const [resultIndex, result] of this.cwrRemixResults.entries()) {
			this.onCwrRemixResultApplied?.({
				actionKey: 'aifc-adf-update',
				invokedFrom: this.cwrRemixInvokedFrom,
				result,
				singleInstrumentationId: getCwrRemixResultInstrumentationId(
					this.cwrRemixSingleInstrumentationId,
					resultIndex,
				),
			});
			if (result.previousLocalId && result.previousResultId) {
				this.pendingCwrRemixReplacements.delete(
					cwrRemixReplacementKey({
						previousLocalId: result.previousLocalId,
						previousResultId: result.previousResultId,
					}),
				);
			}
		}
		this.cwrRemixResults = [];
		if (this.cwrRemixSingleInstrumentationId) {
			this.cwrRemixVisualMetadataByInstrumentationId.delete(this.cwrRemixSingleInstrumentationId);
		}
	}

	private collectCwrRemixResultsFromV2Envelope(envelope: ToolCallV2Envelope): void {
		if (!this.cwrRemixInvokedFrom || envelope.status !== 'complete') {
			return;
		}

		const value = envelope.arguments?.value;
		if (typeof value === 'string') {
			const previous = this.v2PreviousCwrRemixIdentityByToolCallId.get(envelope.id);
			const resultsWithPrevious = this.withPreviousCwrRemixResult(
				this.filterNewCwrRemixResults(
					extractCwrRemixResults(envelope.name, value, this.getCwrRemixVisualMetadata()),
					this.v2CwrVisualIdentityKeysByToolCallId.get(envelope.id) ?? new Set(),
				),
				previous,
			);
			this.cwrRemixResults.push(...resultsWithPrevious);
			if (!resultsWithPrevious.some((result) => result.previousResultId)) {
				this.cancelCwrRemixReplacement(previous);
			}
		} else {
			this.cancelCwrRemixReplacement(this.v2PreviousCwrRemixIdentityByToolCallId.get(envelope.id));
		}
	}

	abort(reason?: AbortReason, options?: AbortOptions): void {
		// `error` is a new, telemetry-only reason. Everything downstream of the
		// orchestrator predates it, so collapse it to the `aborted` it used to
		// arrive as and leave the debugger and error-key payloads untouched.
		const legacyReason: Exclude<AbortReason, 'error'> | undefined =
			reason === 'error' ? 'aborted' : reason;
		this.debugger?.endSession(
			legacyReason === 'user_abort' ? 'aborted' : (legacyReason ?? 'aborted'),
		);
		// Propagate an error to any in-flight v2 processor so the orchestrator
		// dispatches an error event for the last partial snapshot rather than
		// silently committing it. Matches the error-propagation semantics of
		// the v1 path's `finishAction(actionId, error)` flow.
		this.reset({
			failureReason: FAILURE_REASON.UNKNOWN,
			errorKey: legacyReason ?? 'aborted',
		});
		this.editorApi.aiStreamingOrchestrator?.actions.abortSession({
			reason: toSessionAbortReason(reason),
			errorMessage: options?.errorMessage,
		});
	}

	setActionMetadata(metadata: ActionMetadata): void {
		this.actionMetadata = metadata;
		log('Set action metadata', { metadata });
	}
}
